import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import EdilmindLogo from "@/components/EdilmindLogo";
import { formatApiError } from "@/lib/api";
import { Loader2, KeyRound, Eye, EyeOff, ArrowRight, CheckCircle2 } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { submitPasswordReset } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Centered>
        <h2 className="text-lg font-bold text-brand-navy">Link non valido</h2>
        <p className="text-sm text-slate-600 mb-4">Il link di reset password è mancante o malformato.</p>
        <Link to="/login" className="text-brand-orange hover:underline text-sm">← Torna al login</Link>
      </Centered>
    );
  }

  if (done) {
    return (
      <Centered>
        <CheckCircle2 size={42} className="text-emerald-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-brand-navy">Password aggiornata</h2>
        <p className="text-sm text-slate-600">Reindirizzamento in corso...</p>
      </Centered>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (password !== confirm) {
      setErr("Le password non coincidono.");
      return;
    }
    setBusy(true);
    try {
      await submitPasswordReset(token, password);
      setDone(true);
      setTimeout(() => navigate("/"), 1500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Centered>
      <h2 className="text-xl font-bold text-brand-navy mb-1">Reimposta la password</h2>
      <p className="text-xs text-slate-600 mb-5">Scegli una nuova password per il tuo account EDILMIND.</p>
      <form onSubmit={submit} className="space-y-3 text-left" data-testid="reset-form">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 inline-flex items-center gap-1">
            <KeyRound size={14} /> Nuova password
          </span>
          <div className="relative">
            <input
              type={show ? "text" : "password"} autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              disabled={busy} required minLength={8}
              data-testid="reset-password-input"
              className="w-full px-3 py-2.5 pr-10 rounded-md border border-slate-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none text-sm"
            />
            <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
              className="absolute inset-y-0 right-0 px-3 text-slate-400 hover:text-brand-navy">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
            Conferma password
          </span>
          <input
            type={show ? "text" : "password"} autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            disabled={busy} required minLength={8}
            data-testid="reset-confirm-input"
            className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none text-sm"
          />
        </label>

        {err && (
          <div data-testid="reset-error" className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
            {err}
          </div>
        )}

        <button type="submit" disabled={busy}
          data-testid="reset-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-semibold py-2.5 px-4 rounded-md hover:bg-brand-navy/90 disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Aggiorna password
        </button>
      </form>
    </Centered>
  );
};

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
    <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8 text-center">
      <EdilmindLogo size={48} />
      <div className="mt-4">{children}</div>
    </div>
  </div>
);

export default ResetPassword;
